import { Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

export function OnboardingWidget() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [
    hasReviewedCampaignInLocalStorage,
    setHasReviewedCampaignInLocalStorage,
  ] = useState(false);
  const { creatorId } = useAccount();

  // Get onboarding status efficiently in a single query
  const { data: onboardingStatus } = useQuery(
    convexQuery(api.onboarding.getCreatorOnboardingStatus, {}),
  );

  // Check localStorage for campaign review
  useEffect(() => {
    const reviewed = localStorage.getItem("hasReviewedCampaign");
    if (reviewed === "true") {
      setHasReviewedCampaignInLocalStorage(true);
    }
  }, []);

  // Don't render if not a creator
  if (!creatorId) {
    return null;
  }

  // Check completion status based on onboarding data
  const hasJoinedCampaign = onboardingStatus?.hasJoinedCampaign ?? false;

  // hasReviewedCampaign is true if they've joined a campaign OR if localStorage says they've reviewed one
  const hasReviewedCampaign =
    hasJoinedCampaign || hasReviewedCampaignInLocalStorage;

  // Check if user has uploaded videos across ALL their contracts (status !== "draft")
  const hasUploadedVideo = onboardingStatus?.hasUploadedVideo ?? false;

  // Check if user has published videos across ALL their contracts (status === "approved")
  const hasPublishedVideo = onboardingStatus?.hasPublishedVideo ?? false;

  const steps: OnboardingStep[] = [
    {
      id: "review_campaign",
      title: "Review a campaign",
      description: "Click on any campaign to learn more",
      completed: hasReviewedCampaign,
    },
    {
      id: "join_campaign",
      title: "Join a campaign",
      description: 'Click "Get Started" to apply',
      completed: hasJoinedCampaign,
    },
    {
      id: "upload_video",
      title: "Upload a video for brand approval",
      description: "Create and submit your first video",
      completed: hasUploadedVideo,
    },
    {
      id: "publish_video",
      title: "Publish a video",
      description: "Get approved and publish your content",
      completed: hasPublishedVideo,
    },
  ];

  const completedCount = steps.filter((step) => step.completed).length;
  const totalCount = steps.length;
  const progress = (completedCount / totalCount) * 100;
  const allCompleted = completedCount === totalCount;

  // Don't render if all steps are completed
  if (allCompleted) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-40">
      {!isExpanded ? (
        // Minimized: Circular button with progress
        <button
          onClick={() => setIsExpanded(true)}
          className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary/20 bg-card shadow-lg transition-all hover:border-primary/40 hover:shadow-xl"
        >
          <span className="text-sm font-semibold text-primary">
            {completedCount}/{totalCount}
          </span>
        </button>
      ) : (
        // Expanded: Full widget
        <div className="w-80 rounded-lg border border-border bg-card shadow-lg">
          {/* Header */}
          <div className="flex cursor-pointer items-center justify-between border-b border-border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-semibold text-primary">
                  {completedCount}/{totalCount}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary">
                  Get Started
                </h3>
                <p className="text-xs text-primary/60">
                  {completedCount} of {totalCount} steps completed
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="px-4 pt-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-primary/10">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Checklist */}
          <div className="p-4 space-y-3">
            {steps.map((step) => (
              <div
                key={step.id}
                className={cn(
                  "flex items-start gap-3 rounded-md p-2 transition-colors",
                  step.completed ? "opacity-60" : "hover:bg-primary/5",
                )}
              >
                <div
                  className={cn(
                    "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2",
                    step.completed
                      ? "border-green-600 bg-green-600"
                      : "border-primary/30 bg-transparent",
                  )}
                >
                  {step.completed && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      step.completed
                        ? "text-primary/60 line-through"
                        : "text-primary",
                    )}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-primary/60">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
